import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useT } from './i18n.jsx';

/**
 * Aide contextuelle repliée derrière un « ? ».
 *
 * Elle existe pour désencombrer, et elle ne convient PAS à tout. Ce qui doit
 * être lu AVANT d'agir — un avertissement de séquence, un diagnostic — reste en
 * clair : une bulle ne se survole que par quelqu'un qui sait déjà qu'il y a
 * quelque chose dessous. Ne la réserver qu'au descriptif, celui qu'on lit une
 * fois et jamais plus.
 *
 * Positionnement en `fixed`, calculé à l'ouverture. Les deux interfaces logent
 * leurs contrôles dans des conteneurs à `overflow: auto` — un panneau de 340 px
 * à gauche, des listes à droite : en `absolute`, la bulle y serait rognée ou
 * ferait apparaître une barre de défilement horizontale. `fixed` échappe au
 * conteneur, au prix d'une fermeture au défilement — qui est de toute façon le
 * comportement attendu.
 */

const LARGEUR = 300;
const MARGE = 12;
const ECART = 6;

export function Bulle({ children }: { children: ReactNode }): ReactElement {
  const t = useT();
  const id = useId();
  const declencheur = useRef<HTMLButtonElement>(null);
  const contenu = useRef<HTMLSpanElement>(null);
  const [ancre, setAncre] = useState<DOMRect | null>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);

  const ouvrir = useCallback((): void => {
    const rect = declencheur.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setAncre(rect);
  }, []);

  const fermer = useCallback((): void => {
    setAncre(null);
    setPlace(null);
  }, []);

  /*
   * Deux passes : la première rend la bulle hors écran pour la mesurer, la
   * seconde la place. Sans sa hauteur réelle on ne peut pas savoir si elle
   * tient sous le déclencheur — et le panneau descend jusqu'au bas de la
   * fenêtre, où une bulle ouverte vers le bas est coupée sans recours.
   */
  useLayoutEffect(() => {
    if (ancre === null) return;
    const hauteur = contenu.current?.offsetHeight ?? 0;
    const sousLeDeclencheur = ancre.bottom + ECART;
    setPlace({
      left: Math.max(MARGE, Math.min(ancre.left, window.innerWidth - LARGEUR - MARGE)),
      top:
        sousLeDeclencheur + hauteur + MARGE <= window.innerHeight
          ? sousLeDeclencheur
          : Math.max(MARGE, ancre.top - ECART - hauteur),
      width: LARGEUR,
    });
  }, [ancre]);

  /*
   * Le défilement et le redimensionnement invalident la position calculée.
   * L'écoute est en phase de capture pour attraper aussi le défilement des
   * conteneurs internes, qui ne remonte pas jusqu'à window.
   */
  useEffect(() => {
    if (ancre === null) return undefined;
    window.addEventListener('scroll', fermer, true);
    window.addEventListener('resize', fermer);
    return (): void => {
      window.removeEventListener('scroll', fermer, true);
      window.removeEventListener('resize', fermer);
    };
  }, [ancre, fermer]);

  return (
    <span className="bulle">
      <button
        ref={declencheur}
        type="button"
        className="bulle__declencheur"
        aria-label={t('Aide')}
        aria-describedby={ancre === null ? undefined : id}
        aria-expanded={ancre !== null}
        onPointerEnter={ouvrir}
        onPointerLeave={fermer}
        onFocus={ouvrir}
        onBlur={fermer}
        onKeyDown={(event): void => {
          if (event.key === 'Escape') fermer();
        }}
        onClick={(event): void => {
          // Le clic ne doit rien soumettre ni replier le <details> qui l'entoure.
          event.preventDefault();
        }}
      >
        ?
      </button>
      {ancre !== null && (
        <span
          ref={contenu}
          id={id}
          role="tooltip"
          className="bulle__contenu"
          style={place ?? { top: 0, left: 0, width: LARGEUR, visibility: 'hidden' }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
